import {useState, useEffect, useRef} from 'react';
import {useAtom} from 'jotai';
import {useQuestionToggleAtom} from '../../atoms/useQuestionToggleAtom';
import {useSelectedTabAtom, selectedTabAtom} from '../../atoms/useSelectedTabAtom';
import {selectedEnvironmentAtom} from '../../atoms/useSelectedEnvironmentAtom';
import {allowStopAtom} from '../../atoms/useAllowStopAtom';
import {isStoppingAtom} from '../../atoms/useIsStoppingAtom';
import {appenedChatItem} from '../../services/appendChatItem';
import {sendMessageToBE} from '../../api/questionAPIs';
import {nanoid} from 'nanoid';
import {useTranslation} from 'react-i18next';

import MessageBubble from '../message/MessageBubble';
import bot from '../../assets/bot.jpg';
import avatar from '../../assets/avatar.jpg';
import {IntentEnum} from "../../enums/IntentEnum";
import {MessageTypeEnum} from "../../enums/MessageTypeEnum";
import {useMessageHistoryAtom} from "../../atoms/useMessageHistoryAtom";

const ChatComponent = ({chapter, sourceText, compareResult}) => {
    const [t, i18n] = useTranslation('global');
    const [selectedEnvironment, setSelectedEnvironment] = useAtom(selectedEnvironmentAtom);
    const [isQuestionsVisible, setIsQuestionsVisible] = useQuestionToggleAtom();
    const [selectedTab, setSelectedTab] = useSelectedTabAtom();
    const [messageHistory, setMessageHistory] = useState([]);
    const [allowStop, setAllowStop] = useAtom(allowStopAtom);
    const [allowReset, setAllowReset] = useState(false);
    const [isStopping, setIsStopping] = useAtom(isStoppingAtom);
    const [lastSentMessage, setLastSentMessage] = useState([]);

    const messageListRef = useRef(null);
    const [formData, setFormData] = useState('');
    const [result, setResult] = useState({});

    useEffect(() => {
        console.log('ChatComponent chapter', chapter);
        resetMessageHistory();
    }, [sourceText, chapter, compareResult, selectedTab]);

    useEffect(
        () => () => {
            const history = fillMessageHistoryAfterReset();
            setMessageHistory(history);
        }, []);

    useEffect(() => {
        console.log('is stopping? ' + isStopping);
    }, [isStopping])

    useEffect(() => {
        // Scroll to the bottom when messages change
        if (messageListRef.current) {
            messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
        }

        const history = messageHistory.find(item => item.intent === selectedTab);

        if (!history) {
            setAllowStop(false);
            setAllowReset(false);
            return;
        }

        let currentChat = history.chat;
        let lastMessage = !!currentChat ? currentChat[currentChat.length - 1] : [];
        if (currentChat.length > 1) {
            setAllowReset(true);
        }

        if (lastMessage && lastMessage.Type === 'loading') {
            setAllowReset(false);
            setAllowStop(true);
        } else {
            setAllowStop(false);
        }
    }, [messageHistory, selectedTab]);

    useEffect(() => {

        handleResponse(result);
    }, [result]);


    const removeLoading = (history, selectedTab) => {
        const chat = history.find(item => item.intent === selectedTab)?.chat;
        if (chat && chat.length > 0) {
            const lastMessage = chat[chat.length - 1];
            if (lastMessage && lastMessage.Type === MessageTypeEnum.Loading) {
                chat.pop();
            }
        }
    }

    const handleResponse = (message) => {

        removeLoading(messageHistory, selectedTab);

        if (message.Type === 0) {
            console.log('type text');
            setMessageHistory(
                appenedChatItem(
                    selectedTab,
                    {
                        Message: message.Message,
                        sent: formatDate(new Date()),
                        Role: message.Role,
                        Type: message.Type,
                        id: nanoid(),
                    },
                    messageHistory
                )
            );
        }
    }
    const handleChange = (e) => {
        setFormData(e.target.value);
    };

    const toggleQuestions = () => {
        setIsQuestionsVisible((prevState) => !prevState);
    };

    const handleInputKeyDown = (e) => {
        if (e.key === 'ArrowUp') {
            const data = lastSentMessage.find((item) => item.intent === selectedTab);
            if (!data) return;
            setFormData(data.message);

            setTimeout(() => {
                const inputField = document.querySelector('.msger-input');
                if (inputField) {
                    inputField.setSelectionRange(data.message.length, data.message.length);
                }
            }, 0);
        }
    };

    function formatDate(date) {
        const h = '0' + date.getHours();
        const m = '0' + date.getMinutes();

        return `${h.slice(-2)}:${m.slice(-2)}`;
    }

    const handleStop = (e) => {
        e.preventDefault();
        setIsStopping(true);
        const history = messageHistory.find(item => item.intent === selectedTab);

        if (!history) {
            return;
        }

        let currentChat = history.chat;
        let lastMessage = currentChat[currentChat.length - 1];
        if (lastMessage.Type === 'loading') {
            lastMessage.Type = 'stopping';
        }

    }

    const resetMessageHistory = () => {

        const history = fillMessageHistoryAfterReset();

        setMessageHistory(history);
    }

    const fillMessageHistoryAfterReset = () => {
        switch (selectedTab) {
            case IntentEnum.FreeQuestion:
                return [{
                    intent: selectedTab,
                    chat: [
                        {
                            id: nanoid(),
                            Role: 'assistant',
                            Type: MessageTypeEnum.System,
                            Message: JSON.stringify({
                                SourceText: sourceText,
                                ComparedText: chapter?.Chunk ?? ''
                            }),
                            sent: formatDate(new Date()),

                        },
                        {
                            id: nanoid(),
                            Role: 'assistant',
                            Type: MessageTypeEnum.Text,
                            Message: t('messages.askAboutTexts'),
                            sent: formatDate(new Date()),
                        }
                    ]
                }];
            case IntentEnum.SendQuestion: {
                return [{
                    intent: IntentEnum.SendQuestion,
                    chat: [
                        {
                            ...compareResult,
                            intent: IntentEnum.SendQuestion
                        }
                    ]
                }];
            }

        }
    }

    const handleReset = (e) => {
        e.preventDefault();
        resetMessageHistory();
        setAllowReset(false);
        setIsStopping(false);
    }

    const handleSubmit = (e) => {
        e.preventDefault();

        if (formData) {
            const newId = nanoid();

            const history =
                appenedChatItem(
                    selectedTab,
                    {Message: formData, sent: formatDate(new Date()), Role: 'user', Type: 0, id: newId},
                    messageHistory
                );
            setMessageHistory(history);

            const completeHistory = 
            appenedChatItem(
                selectedTab,  
                {Message: 'Loading', sent: formatDate(new Date()), Role: 'assistant', Type: MessageTypeEnum.Loading, id: newId},
                history
            );
            setMessageHistory(completeHistory);

            sendMessageToBE(selectedTab, completeHistory, formData, newId, sourceText, chapter.Chunk)
                .then((message) => {
                    setResult(message);
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                });
        }
        setAllowStop(false);
        setIsStopping(false);
        setFormData('');
    };

    return (
        <div ref={messageListRef} className={`context-wrap focus`}>
            <main className="msger-chat">
                {messageHistory
                    .filter((item) => item.intent === selectedTab)
                    .map((item) =>
                        item.chat.filter(c => c.Type === MessageTypeEnum.Text || c.Type === MessageTypeEnum.Loading).map((item) => (
                            <MessageBubble
                                key={item.id}
                                id={item.id}
                                type={item.Type}
                                message={item.Message}
                                messageId={item.MessageID}
                                meta={item.Metadata}
                                sent={item.sent}
                                role={item.Role}
                                bot={bot}
                                avatar={avatar}
                            />
                        ))
                    )}
                {allowReset ?
                <div className='reset-area' onClick={handleReset}>
                    <label>Reset</label>
                    <span className='material-symbols-outlined'>restart_alt</span>
                </div> : null}
            </main>
            {allowStop === true ?
                <div className='allowstop-area' onClick={handleStop}>
                    {isStopping ? <label>{t('stopping')}</label> : <label>{t('stopResponding')}</label>}
                    <span className='material-symbols-outlined'>stop_circle</span>
                </div>
                : null}
            <form className="msger-inputarea" onSubmit={handleSubmit}>
                <input
                    type="text"
                    className="msger-input"
                    placeholder={t('enterMessage')}
                    name="inputName"
                    value={formData}
                    autoComplete="off"
                    onChange={handleChange}
                    onKeyDown={handleInputKeyDown}

                />
                <button type="submit" className="msger-send-btn">
                    <span className="material-symbols-outlined">arrow_upward_alt</span>
                    <span className="send-text">{t('send')}</span>
                </button>
            </form>
        </div>
    );
};

export default ChatComponent;
